<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import Icon from '@/components/Icon.vue'
import { useNotify } from '@/stores/notify'
import { usePresence } from '@/stores/presence'
import type { InboxItem, SignalKind } from '@/types/atrium'

const notify = useNotify()
const presence = usePresence()
const router = useRouter()

const KIND: Record<SignalKind, { icon: string; tint: string; verb: string }> = {
  mention: { icon: 'note', tint: 'var(--gold-bright)', verb: 'mentioned you' },
  knock: { icon: 'knock', tint: 'var(--p-cowork)', verb: 'is knocking' },
  roomknock: { icon: 'knock', tint: 'var(--p-meeting)', verb: '' },
  grab: { icon: 'grab', tint: 'var(--p-open)', verb: '' },
  enter: { icon: 'door', tint: 'var(--p-meeting)', verb: '' },
}

const items = computed(() => notify.inbox)

function trim(t: string | undefined): string {
  const s = String(t || '').replace(/\s+/g, ' ').trim()
  return s.length > 90 ? s.slice(0, 88) + '…' : s
}
function go(it: InboxItem) {
  it.read = true
  // Follow items (knock accepts) chase the person's LIVE room; the stored
  // slug is only the fallback from accept time.
  if (it.follow && it.from) {
    notify.close()
    notify.goToPerson(it.from, it.slug)
    return
  }
  if (it.slug) { notify.close(); void router.push(`/room/${it.slug}`) }
}
</script>

<template>
  <div class="inbox-back" @click="notify.close()" />
  <div class="inbox-panel in">
    <div class="ib-head">
      <div class="row gap-2"><Icon name="bell" :size="18" /><b>Inbox</b><span v-if="notify.inboxUnread" class="ib-count">{{ notify.inboxUnread }}</span></div>
      <div class="row gap-2">
        <button v-if="items.length" class="ib-mark" @click="notify.markAllRead()">Mark all read</button>
        <button class="icon-mini" @click="notify.close()"><Icon name="x" :size="16" /></button>
      </div>
    </div>
    <div v-if="presence.isDnd" class="ib-dnd"><Icon name="moon" :size="14" /> Heads-down — knocks are held, notices are muted.</div>
    <div class="ib-list">
      <div v-if="!items.length" class="ib-empty"><Icon name="bell" :size="20" /><div>You’re all caught up.</div></div>
      <div v-for="it in items" :key="it.id" class="ib-item" :class="{ 'is-read': it.read }" @click="go(it)">
        <span class="ib-ico" :style="`color:${KIND[it.kind].tint}`"><Icon :name="KIND[it.kind].icon" :size="16" /></span>
        <div class="ib-body">
          <div class="ib-line">
            <b>{{ it.fromName || 'Someone' }}</b> {{ KIND[it.kind].verb }}
            <span v-if="it.text && it.kind !== 'knock'" class="ib-ctx">{{ trim(it.text) }}</span>
          </div>
          <div v-if="it.kind === 'knock' && it.text" class="ib-ctx">“{{ trim(it.text) }}”</div>
          <div class="ib-time">{{ notify.ago(it.ts) }}</div>
        </div>
        <button v-if="it.kind === 'knock' && !it.accepted" class="ib-act" @click.stop="notify.acceptKnock(it.id)">
          <Icon name="check" :size="14" /> Accept
        </button>
        <button v-else-if="it.kind === 'roomknock' && !it.accepted" class="ib-act" @click.stop="notify.admitKnock(it.id)">
          <Icon name="door" :size="14" /> Let in
        </button>
        <button v-else-if="it.slug || it.follow" class="ib-act ghost" @click.stop="go(it)">{{ it.follow ? 'Join' : 'Open' }}</button>
      </div>
    </div>
  </div>
</template>
